import { varAlpha } from 'minimal-shared/utils';

import { listClasses } from '@mui/material/List';
import { paperClasses } from '@mui/material/Paper';
import { textFieldClasses } from '@mui/material/TextField';
import { inputBaseClasses } from '@mui/material/InputBase';
import { inputLabelClasses } from '@mui/material/InputLabel';
import { iconButtonClasses } from '@mui/material/IconButton';
import SvgIcon, { svgIconClasses } from '@mui/material/SvgIcon';
import { listItemIconClasses } from '@mui/material/ListItemIcon';
import { circularProgressClasses } from '@mui/material/CircularProgress';
import { formControlLabelClasses } from '@mui/material/FormControlLabel';

// ----------------------------------------------------------------------

const MuiDataGrid = {
  /** DEFAULT PROPS */
  defaultProps: {
    slots: {
      /* Column */
      columnSortedAscendingIcon: (props) => (
        <DataGridArrowUpIcon sx={{ color: 'text.primary' }} {...props} />
      ),
      columnSortedDescendingIcon: (props) => (
        <DataGridArrowDownIcon sx={{ color: 'text.primary' }} {...props} />
      ),
      columnUnsortedIcon: (props) => (
        <DataGridArrowUpIcon
          fontSize={props.fontSize}
          className={props.className}
          sx={{ color: 'text.disabled' }}
        />
      ),
      columnMenuIcon: (props) => <DataGridMoreIcon width={20} {...props} />,
      columnMenuSortAscendingIcon: (props) => <DataGridArrowUpIcon {...props} />,
      columnMenuSortDescendingIcon: (props) => <DataGridArrowDownIcon {...props} />,
      columnMenuFilterIcon: (props) => <DataGridFilterIcon {...props} />,
      columnMenuHideIcon: (props) => <DataGridEyeCloseIcon {...props} />,
      columnMenuManageColumnsIcon: (props) => <DataGridEyeIcon {...props} />,
      columnSelectorIcon: (props) => <DataGridEyeIcon {...props} />,
      /* Filter */
      filterPanelDeleteIcon: (props) => <DataGridCloseIcon {...props} />,
      openFilterButtonIcon: (props) => <DataGridFilterIcon {...props} />,
      columnFilteredIcon: (props) => (
        <DataGridFilterIcon sx={{ width: 16, color: 'text.primary' }} {...props} />
      ),
      /* Density */
      densityCompactIcon: (props) => <DataGridDensityCompactIcon {...props} />,
      densityStandardIcon: (props) => <DataGridDensityStandardIcon {...props} />,
      densityComfortableIcon: (props) => <DataGridDensityComfortableIcon {...props} />,
      /* Export */
      exportIcon: (props) => <DataGridExportIcon {...props} />,
      /*  Quick Filter */
      quickFilterIcon: (props) => (
        <DataGridSearchIcon sx={{ width: 24, height: 24, color: 'text.secondary' }} {...props} />
      ),
      quickFilterClearIcon: (props) => <DataGridCloseIcon {...props} />,
    },
    slotProps: {
      basePopper: { placement: 'bottom-end' },
      baseChip: { size: 'small' },
      baseSwitch: { size: 'small' },
      baseCheckbox: { size: 'small', disableRipple: true },
      baseInputLabel: { shrink: true },
      baseTextField: { variant: 'outlined' },
      baseSelect: { native: true, variant: 'outlined' },
    },
  },

  /** STYLE */
  styleOverrides: {
    root: ({ theme }) => {
      const styles = {
        pinnedCell: {
          common: {
            backgroundColor: theme.vars.palette.background.paper,
            '&::after': { backgroundColor: theme.vars.palette.action.hover },
          },
          selected: {
            backgroundColor: theme.vars.palette.background.paper,
            '&::after': {
              backgroundColor: varAlpha(
                theme.vars.palette.error.mainChannel,
                theme.vars.palette.action.selectedOpacity
              ),
            },
          },
        },
      };

      return {
        '--unstable_DataGrid-radius': 0,
        '--DataGrid-rowBorderColor': theme.vars.palette.divider,
        '--DataGrid-containerBackground': theme.vars.palette.background.neutral,
        '--unstable_DataGrid-headWeight': theme.typography.fontWeightSemiBold,
        borderWidth: 0,
        scrollbarWidth: 'thin',
        scrollbarColor: `${varAlpha(theme.vars.palette.text.disabledChannel, 0.4)} ${varAlpha(theme.vars.palette.text.disabledChannel, 0.08)}`,
        '& .MuiDataGrid-filler > div': { borderTopStyle: 'dashed' },
        '& .MuiDataGrid-topContainer::after': { height: 0 },
        '& .MuiTablePagination-input': { display: 'block' },
        '& .MuiDataGrid-virtualScrollerContent': {
          '& .MuiDataGrid-row': {
            '&:hover': {
              '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight':
                styles.pinnedCell.common,
            },
            '&.Mui-selected': {
              '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight':
                styles.pinnedCell.selected,
              '&:hover .MuiDataGrid-cell--pinnedLeft, &:hover .MuiDataGrid-cell--pinnedRight':
                styles.pinnedCell.selected,
            },
          },
        },
      };
    },
    withBorderColor: { borderColor: 'var(--DataGrid-rowBorderColor)' },
    /** Column */
    columnHeader: ({ theme }) => ({
      fontSize: 14,
      color: theme.vars.palette.text.secondary,
      '&--sorted': { color: theme.vars.palette.text.primary },
    }),
    columnSeparator: { color: 'var(--DataGrid-rowBorderColor)' },
    /** Row, Cell */
    cell: ({ theme }) => ({
      borderTopStyle: 'dashed',
      '&--editing': {
        boxShadow: 'none',
        backgroundColor: varAlpha(theme.vars.palette.error.mainChannel, 0.08),
      },
      '&--pinnedLeft, &--pinnedRight': {
        '&::after': {
          top: 0,
          left: 0,
          zIndex: -1,
          content: "''",
          width: '100%',
          height: '100%',
          position: 'absolute',
        },
      },
    }),
    /** Toolbar */
    toolbarContainer: ({ theme }) => ({
      gap: theme.spacing(2),
      padding: theme.spacing(2),
      [`& .${textFieldClasses.root}`]: {
        padding: 0,
        width: '100%',
        [`& .${inputBaseClasses.input}`]: {
          paddingTop: theme.spacing(2),
          paddingBottom: theme.spacing(2),
        },
        [theme.breakpoints.up('md')]: { width: 'unset' },
      },
    }),
    /** Paper */
    paper: ({ theme }) => ({ ...theme.mixins.paperStyles(theme, { dropdown: true }), padding: 0 }),
    menu: ({ theme }) => ({
      [`& .${paperClasses.root}`]: {
        ...theme.mixins.paperStyles(theme, { dropdown: true }),
        minWidth: 140,
      },
      [`& .${listClasses.root}`]: {
        padding: 0,
        [`& .${listItemIconClasses.root}`]: { minWidth: 0, marginRight: theme.spacing(2) },
      },
    }),
    /** Icons */
    menuIcon: ({ theme }) => ({
      [`& .${iconButtonClasses.root}`]: {
        margin: theme.spacing(0, 1),
        padding: theme.spacing(0.25),
      },
    }),
    iconButtonContainer: ({ theme }) => ({
      [`& .${iconButtonClasses.root}`]: {
        padding: theme.spacing(0.25),
        marginLeft: theme.spacing(1),
      },
    }),
    /** Footer */
    footerContainer: { minHeight: 'auto', borderTopStyle: 'dashed' },
    selectedRowCount: { display: 'none', whiteSpace: 'nowrap' },
    overlay: ({ theme }) => ({
      [`& .${circularProgressClasses.root}`]: { color: theme.vars.palette.text.primary },
    }),
    /** Column panel */
    columnsManagementHeader: ({ theme }) => ({
      padding: theme.spacing(2.5, 2, 0, 2),
      [`& .${inputBaseClasses.input}`]: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
      },
    }),
    columnsManagement: ({ theme }) => ({
      gap: theme.spacing(0.5),
      padding: theme.spacing(2, 1.5),
      [`& .${formControlLabelClasses.root}`]: { gap: 4, marginLeft: 0 },
    }),
    columnsManagementFooter: ({ theme }) => ({
      borderTopStyle: 'dashed',
      padding: theme.spacing(1.5),
      [`& .${formControlLabelClasses.root}`]: { gap: 4, marginLeft: 0 },
    }),
    /** Filter panel */
    filterForm: ({ theme }) => ({
      alignItems: 'center',
      gap: theme.spacing(1.5),
      padding: theme.spacing(2),
      [theme.breakpoints.down('md')]: { flexDirection: 'column', alignItems: 'flex-end' },
      /* Fix label with input variant === 'outlined' */
      [`& .${inputLabelClasses.shrink}`]: { transform: 'translate(14px, -9px) scale(.75)' },
    }),
    filterFormDeleteIcon: ({ theme }) => ({
      [`& .${iconButtonClasses.root}`]: {
        padding: theme.spacing(0.25),
        backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.16),
        [`& .${svgIconClasses.root}`]: { width: 16, height: 16 },
      },
    }),
    filterFormColumnInput: ({ theme }) => ({
      [theme.breakpoints.down('md')]: { width: '100%' },
    }),
    filterFormOperatorInput: ({ theme }) => ({
      [theme.breakpoints.down('md')]: { width: '100%' },
    }),
    filterFormValueInput: ({ theme }) => ({
      [theme.breakpoints.down('md')]: { width: '100%' },
    }),
  },
};

export const dataGrid = { MuiDataGrid };

const svgIconProps = (props) => ({
  ...props,
  sx: [{ width: 20, height: 20 }, ...(Array.isArray(props?.sx) ? (props?.sx ?? []) : [props?.sx])],
});

/**
 * Icons
 * @see https://icon-sets.iconify.design/solar/alt-arrow-up-bold-duotone
 */
const DataGridArrowUpIcon = (props) => (
  <SvgIcon {...svgIconProps(props)}>
    <path
      fill="currentColor"
      d="m8.3 11.6 3.3-3.4a.5.5 0 0 1 .8 0l6.4 6.6c.4.4.2 1.2-.4 1.2h-5.7z"
    />
    <path fill="currentColor" d="M11.3 16H5.6c-.6 0-.8-.8-.4-1.2l2.4-2.5z" opacity="0.5" />
  </SvgIcon>
);

/**
 * Icons
 * @see https://icon-sets.iconify.design/solar/alt-arrow-down-bold-duotone
 */
const DataGridArrowDownIcon = (props) => (
  <SvgIcon {...svgIconProps(props)}>
    <path fill="currentColor" d="m8.3 12.4 3.3 3.4q.4.4.8 0l6.4-6.6c.4-.4.2-1.2-.4-1.2h-5.7z" />
    <path fill="currentColor" d="M11.3 8H5.6c-.6 0-.8.8-.4 1.2l2.4 2.5z" opacity="0.5" />
  </SvgIcon>
);

/**
 * Icons
 * @see https://icon-sets.iconify.design/solar/filter-bold
 */
const DataGridFilterIcon = (props) => (
  <SvgIcon {...svgIconProps(props)}>
    <path
      fill="currentColor"
      d="M19 3H5c-1.41 0-2.12 0-2.56.41S2 4.5 2 5.81v.7c0 1.03 0 1.55.26 1.98s.73.7 1.68 1.23l2.91 1.64c.64.36.96.54 1.19.74q.7.6.9 1.49c.06.28.06.62.06 1.28v2.67c0 .91 0 1.37.25 1.72.25.36.7.53 1.6.88 1.88.73 2.81 1.1 3.48.68.67-.41.67-1.37.67-3.28v-2.67c0-.66 0-1 .06-1.28a2.7 2.7 0 0 1 .9-1.5c.23-.19.55-.37 1.19-.73l2.9-1.64c.96-.53 1.43-.8 1.69-1.23S22 7.54 22 6.5v-.69c0-1.32 0-1.99-.44-2.4S20.42 3 19 3"
    />
  </SvgIcon>
);

/**
 * Icons
 * @see https://icon-sets.iconify.design/solar/export-bold
 */
const DataGridExportIcon = (props) => (
  <SvgIcon {...svgIconProps(props)}>
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M8.85 7.9a.75.75 0 0 0 1.06 0l1.72-1.71v8.18a.75.75 0 0 0 1.5 0V6.2l1.72 1.72a.75.75 0 1 0 1.06-1.06l-3-3a.75.75 0 0 0-1.06 0l-3 3a.75.75 0 0 0 0 1.06"
      clipRule="evenodd"
    />
    <path
      fill="currentColor"
      d="M12.38 20.38a8 8 0 0 0 8-8h-3.75c-.95 0-1.42 0-1.71.29-.3.3-.3.76-.3 1.7a2.25 2.25 0 0 1-4.5 0c0-.94 0-1.4-.29-1.7-.3-.3-.76-.3-1.7-.3H4.37a8 8 0 0 0 8 8"
    />
  </SvgIcon>
);

/**
 * Icons
 * @see https://icon-sets.iconify.design/solar/eye-bold
 */
const DataGridEyeIcon = (props) => (
  <SvgIcon {...svgIconProps(props)}>
    <path fill="currentColor" d="M9.75 12a2.25 2.25 0 1 1 4.5 0 2.25 2.25 0 0 1-4.5 0" />
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M2 12c0 1.64.43 2.2 1.27 3.3C4.97 17.5 7.82 20 12 20s7.03-2.5 8.73-4.7C21.57 14.2 22 13.64 22 12s-.42-2.2-1.27-3.3C19.03 6.5 16.18 4 12 4S4.97 6.5 3.28 8.7C2.43 9.81 2 10.36 2 12m10-3.75a3.75 3.75 0 1 0 0 7.5 3.75 3.75 0 0 0 0-7.5"
      clipRule="evenodd"
    />
  </SvgIcon>
);

/**
 * Icons
 * @see https://icon-sets.iconify.design/ph/eye-closed-bold
 */
const DataGridEyeCloseIcon = (props) => (
  <SvgIcon {...svgIconProps(props)}>
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M1.606 6.08a1 1 0 0 1 1.313.526L2 7l.92-.394v-.001l.003.009.021.045.094.194c.086.172.219.424.4.729a13.4 13.4 0 0 0 1.67 2.237 12 12 0 0 0 .59.592C7.18 11.8 9.251 13 12 13a8.7 8.7 0 0 0 3.22-.602c1.227-.483 2.254-1.21 3.096-1.998a13 13 0 0 0 2.733-3.725l.027-.058.005-.011a1 1 0 0 1 1.838.788L22 7l.92.394-.003.005-.004.008-.011.026-.04.087a14 14 0 0 1-.741 1.348 15.4 15.4 0 0 1-1.711 2.256l.797.797a1 1 0 0 1-1.414 1.415l-.84-.84a12 12 0 0 1-1.897 1.256l.782 1.202a1 1 0 1 1-1.676 1.091l-.986-1.514c-.679.208-1.404.355-2.176.424V16.5a1 1 0 0 1-2 0v-1.544c-.775-.07-1.5-.217-2.177-.425l-.985 1.514a1 1 0 0 1-1.676-1.09l.782-1.203c-.7-.37-1.332-.8-1.897-1.257l-.84.84a1 1 0 0 1-1.414-1.414l.797-.797a15.4 15.4 0 0 1-1.87-2.519 14 14 0 0 1-.591-1.107l-.033-.072-.01-.021-.002-.007-.001-.002v-.001C1.08 7.395 1.08 7.394 2 7l-.919.395a1 1 0 0 1 .525-1.314"
      clipRule="evenodd"
    />
  </SvgIcon>
);

/**
 * Icons
 * @see https://icon-sets.iconify.design/eva/search-fill
 */
const DataGridSearchIcon = (props) => (
  <SvgIcon {...svgIconProps(props)}>
    <path
      fill="currentColor"
      d="m20.7 19.3-3.4-3.4A8 8 0 0 0 19 11a8 8 0 1 0-8 8 8 8 0 0 0 4.9-1.7l3.4 3.4a1 1 0 0 0 1.4 0 1 1 0 0 0 0-1.4M5 11a6 6 0 1 1 6 6 6 6 0 0 1-6-6"
    />
  </SvgIcon>
);

/**
 * Icons
 * @see https://icon-sets.iconify.design/eva/close-fill
 */
const DataGridCloseIcon = (props) => (
  <SvgIcon {...svgIconProps(props)}>
    <path
      fill="currentColor"
      d="m13.41 12 4.3-4.29a1 1 0 1 0-1.42-1.42L12 10.59l-4.29-4.3a1 1 0 0 0-1.42 1.42l4.3 4.29-4.3 4.29a1 1 0 0 0 0 1.42 1 1 0 0 0 1.42 0l4.29-4.3 4.29 4.3a1 1 0 0 0 1.42 0 1 1 0 0 0 0-1.42Z"
    />
  </SvgIcon>
);

/**
 * Icons
 * @see https://icon-sets.iconify.design/mingcute/more-1-fill
 */
const DataGridMoreIcon = (props) => (
  <SvgIcon {...svgIconProps(props)}>
    <g fill="none">
      <path d="M24 0v24H0V0zM12.6 23.3h-.2v.5h.2zm.3-.2-.2.1v.5l.2.1zm-.8 0v.6h.2v-.5z" />
      <path
        fill="currentColor"
        d="M5 10a2 2 0 1 1 0 4 2 2 0 0 1 0-4m7 0a2 2 0 1 1 0 4 2 2 0 0 1 0-4m7 0a2 2 0 1 1 0 4 2 2 0 0 1 0-4"
      />
    </g>
  </SvgIcon>
);

/**
 * Icons
 * @see https://icon-sets.iconify.design/material-symbols/table-rows-narrow-rounded
 */
const DataGridDensityCompactIcon = (props) => (
  <SvgIcon {...svgIconProps(props)}>
    <path
      fill="currentColor"
      d="M4 15.5q-.4 0-.7-.3t-.3-.7V14q0-.4.3-.7T4 13h16q.4 0 .7.3t.3.7v.5q0 .4-.3.7t-.7.3zM4 11q-.4 0-.7-.3T3 10v-.5q0-.4.3-.7t.7-.3h16q.4 0 .7.3t.3.7v.5q0 .4-.3.7t-.7.3zm0-4.5q-.4 0-.7-.3T3 5.5V5q0-.4.3-.7T4 4h16q.4 0 .7.3t.3.7v.5q0 .4-.3.7t-.7.3zM4 20q-.4 0-.7-.3T3 19v-.5q0-.4.3-.7t.7-.3h16q.4 0 .7.3t.3.7v.5q0 .4-.3.7t-.7.3z"
    />
  </SvgIcon>
);

/**
 * Icons
 * @see https://icon-sets.iconify.design/mingcute/rows-2-fill
 */
const DataGridDensityComfortableIcon = (props) => (
  <SvgIcon {...svgIconProps(props)}>
    <g fill="none" fillRule="evenodd">
      <path d="M24 0v24H0V0zM12.6 23.3h-.2v.5h.2zm.3-.2-.2.1v.5l.2.1zm-.8 0v.6h.2v-.5z" />
      <path
        fill="currentColor"
        d="M5 3a2 2 0 0 0-2 2v6h18V5a2 2 0 0 0-2-2zm16 10H3v6a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2z"
      />
    </g>
  </SvgIcon>
);

/**
 * Icons
 * @see https://icon-sets.iconify.design/mingcute/rows-4-fill
 */
const DataGridDensityStandardIcon = (props) => (
  <SvgIcon {...svgIconProps(props)}>
    <g fill="none">
      <path d="M24 0v24H0V0zM12.6 23.3h-.2v.5h.2zm.3-.2-.2.1v.5l.2.1zm-.8 0v.6h.2v-.5z" />
      <path
        fill="currentColor"
        d="M21 16v3a2 2 0 0 1-1.8 2H5a2 2 0 0 1-2-1.8V16zm0-6v4H3v-4zm-2-7a2 2 0 0 1 2 2v3H3V5a2 2 0 0 1 2-2z"
      />
    </g>
  </SvgIcon>
);
